import React from 'react';
import './VoteSlider.css';

// Shared vote slider used by posts, comments and articles
// onVote signature: (isUpvote: boolean, explicitWeight: number|null)
const VoteSlider = ({
  voteWeight,
  setVoteWeight,
  onCancel,
  onVote,
  isVoting = false,
  showDownvote = false,
  showDebug = false,
  debugData = {},
  inline = false,
  userHasVoted = false,
  userVoteWeight = null
}) => {
  // close on Escape (guard in case parent didn't pass onCancel)
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && typeof onCancel === 'function') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Inline popover (positioned by container CSS)
  if (inline) {
    return (
      <div className="vote-slider-popup" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="vote-slider-header">
          <span>Vote Weight: {voteWeight}%</span>
        </div>

        <div className="slider-container">
          <input
            type="range"
            min="1"
            max="100"
            value={voteWeight}
            onChange={(e) => setVoteWeight(parseInt(e.target.value, 10))}
            className="vote-slider"
          />

          <div className="slider-scale">
            {[10,20,30,40,50,60,70,80,90,100].map(value => (
              <div key={value} className="scale-mark major" style={{ left: `${value}%` }} onClick={() => setVoteWeight(value)}>
                <div className="scale-line" />
                <span className="scale-label">{value}</span>
              </div>
            ))}
            {[5,15,25,35,45,55,65,75,85,95].map(value => (
              <div key={value} className="scale-mark minor" style={{ left: `${value}%` }} onClick={() => setVoteWeight(value)}>
                <div className="scale-line" />
                <span className="scale-label scale-label-minor">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {showDebug && (
          <div className="vote-debug-info">
            {debugData.voter && <p><strong>Voter:</strong> {debugData.voter}</p>}
            {debugData.author && <p><strong>Author:</strong> {debugData.author}</p>}
            {debugData.permlink && <p><strong>Permlink:</strong> {debugData.permlink}</p>}
            <p><strong>Vote Weight:</strong> {voteWeight}%</p>
          </div>
        )}

        <div className="slider-actions">
          <button className="vote-cancel-btn" onClick={() => { if (typeof onCancel === 'function') onCancel(); }}>Cancel</button>
          <button className="btn btn-sm vote-btn-primary" onClick={() => onVote(true, voteWeight)} disabled={isVoting || (userHasVoted && userVoteWeight != null && voteWeight === userVoteWeight)}>{isVoting ? 'Voting...' : (userHasVoted ? 'Update vote weight (' + userVoteWeight + '% to ' + voteWeight + '%)' : 'Upvote')}</button>
          {showDownvote && (
            <button className="btn btn-sm vote-btn-secondary" onClick={() => onVote(false, voteWeight)} disabled={isVoting}>{isVoting ? 'Voting...' : 'Downvote'}</button>
          )}
        </div>
      </div>
    );
  }

  // Modal (default) behaviour
  return (
    <div className="vote-slider-backdrop" onClick={() => { if (typeof onCancel === 'function') onCancel(); }}>
      <div className="vote-slider-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="vote-slider-modal-inner">
          <div className="vote-slider-header">
            <span>Vote Weight: {voteWeight}%</span>
          </div>

          <div className="slider-container">
            <input
              type="range"
              min="1"
              max="100"
              value={voteWeight}
              onChange={(e) => setVoteWeight(parseInt(e.target.value, 10))}
              className="vote-slider"
            />

            <div className="slider-scale">
              {[10,20,30,40,50,60,70,80,90,100].map(value => (
                <div key={value} className="scale-mark major" style={{ left: `${value}%` }} onClick={() => setVoteWeight(value)}>
                  <div className="scale-line" />
                  <span className="scale-label">{value}</span>
                </div>
              ))}
              {[5,15,25,35,45,55,65,75,85,95].map(value => (
                <div key={value} className="scale-mark minor" style={{ left: `${value}%` }} onClick={() => setVoteWeight(value)}>
                  <div className="scale-line" />
                  <span className="scale-label scale-label-minor">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {showDebug && (
            <div className="vote-debug-info">
              {debugData.voter && <p><strong>Voter:</strong> {debugData.voter}</p>}
              {debugData.author && <p><strong>Author:</strong> {debugData.author}</p>}
              {debugData.permlink && <p><strong>Permlink:</strong> {debugData.permlink}</p>}
              <p><strong>Vote Weight:</strong> {voteWeight}%</p>
            </div>
          )}

          <div className="slider-actions">
            <button className="vote-cancel-btn" onClick={() => { if (typeof onCancel === 'function') onCancel(); }}>Cancel</button>
            <button className="btn btn-sm vote-btn-primary" onClick={() => onVote(true, voteWeight)} disabled={isVoting || (userHasVoted && userVoteWeight != null && voteWeight === userVoteWeight)}>{isVoting ? 'Voting...' : (userHasVoted ? 'Update vote weight (' + userVoteWeight + '% to ' + voteWeight + '%)' : 'Upvote')}</button>
            {showDownvote && (
              <button className="btn btn-sm vote-btn-secondary" onClick={() => onVote(false, voteWeight)} disabled={isVoting}>{isVoting ? 'Voting...' : 'Downvote'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoteSlider;
